import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { AgentsPanel } from "./settings/AgentsPanel";
import { AppearancePanel } from "./settings/AppearancePanel";
import { NotificationsPanel } from "./settings/NotificationsPanel";
import { AboutPanel } from "./settings/AboutPanel";

type SectionId = "agents" | "appearance" | "notifications" | "about";

const SECTIONS: { id: SectionId; label: string }[] = [
  { id: "agents", label: "Agents" },
  { id: "appearance", label: "Appearance" },
  { id: "notifications", label: "Notifications" },
  { id: "about", label: "About" },
];

interface Props {
  open: boolean;
  onClose: () => void;
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function SettingsModal({ open, onClose }: Props) {
  const [section, setSection] = useState<SectionId>("agents");
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const lastFocusedRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;

    // Save the element that triggered the open so we can restore focus on close.
    lastFocusedRef.current = document.activeElement as HTMLElement | null;

    // Move initial focus into the modal.
    closeBtnRef.current?.focus();

    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      // Focus trap: cycle focus inside the modal.
      const root = modalRef.current;
      if (!root) return;
      const focusables = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => !el.hasAttribute("disabled") && getComputedStyle(el).display !== "none"
      );
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      // Restore focus to the trigger element on close.
      const prev = lastFocusedRef.current;
      if (prev && typeof prev.focus === "function") {
        prev.focus();
      }
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="settings-modal-root"
      role="dialog"
      aria-modal="true"
      aria-labelledby="settings-title"
    >
      <div className="settings-backdrop" data-testid="settings-backdrop" onClick={onClose} />
      <div ref={modalRef} className="settings-modal">
        <header className="settings-modal__header">
          <h2 id="settings-title">Settings</h2>
          <button
            ref={closeBtnRef}
            type="button"
            className="settings-modal__close"
            aria-label="Close settings"
            onClick={onClose}
          >
            <X size={16} />
          </button>
        </header>
        <div className="settings-modal__body">
          <nav className="settings-modal__nav" aria-label="Settings sections">
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                type="button"
                className={`settings-modal__nav-btn${s.id === section ? " is-active" : ""}`}
                onClick={() => setSection(s.id)}
              >
                {s.label}
              </button>
            ))}
          </nav>
          <section className="settings-modal__content">
            {section === "agents" && <AgentsPanel />}
            {section === "appearance" && <AppearancePanel />}
            {section === "notifications" && <NotificationsPanel />}
            {section === "about" && <AboutPanel onClose={onClose} />}
          </section>
        </div>
      </div>
    </div>
  );
}
